import { useCallback, useEffect, useRef, useState } from 'react';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useLocation } from 'react-router-dom';

import {
	useAddTaskMutation,
	useDeleteTaskMutation,
	useUpdateOverviewAndStageVisibilityMutation,
	useUpdateTaskMutation
} from '@features/project-management-tools/api/projectManagementApi';
import { calculateNewSl, isHideColumn, isValidRequestBody } from '@features/project-management-tools/utils';
import SortableTaskRow from '@features/project-management-tools/components/SortableTaskRow';
import ProjectAccordionWraper from '@features/project-management-tools/components/ProjectAccordionWraper';
import {
	INITIAL_TASK_STATE,
	MIX_PANEL_EVENTS,
	PROJECT_DETAILS_TABLE_COLUMNS,
	TASK_COLUMNS
} from '@features/project-management-tools/constents';
import CreateAndEditGroup from '@features/project-management-tools/components/CreateAndEditGroup';
import useMultipleOutsideClick from '@features/project-management-tools/hooks/useMultipleOutsideClick';
import DragOverlayForTable from '@features/project-management-tools/components/DragOverlayForTable';
import TaskDndTagsModal from '@features/project-management-tools/components/TaskDndTagsModal';

import CommonImage from '@/components/CommonImage';
import InputField from '@/components/InputField';
import CustomDropdown from '@/components/CustomDropdown';
import SampleComment from '@/components/SampleComment';
import { toastError, toastSuccess } from '@/components/Toast';
import LoaderComponent from '@/components/LoaderComponent';
import { getLoadingState, onGetLocalFromUtc } from '@/utils/Util';
import CustomTooltip from '@/components/CustomTooltip';
import { useMyProjectDetails } from '@/store/reducers';
import { useLazyGetAllUserQuery, useMixPanelTrakingEventMutation } from '@/api/commonApis';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal';

const ProjectDetailsTableView = ({
	groupedTasks,
	id,
	setGroupedTasks,
	refetchGroups,
	refetchTasks,
	refetchProjectDetails,
	refetchProjectTags,
	projectDetails
}) => {
	const { selectedColumnToHide } = useMyProjectDetails();
	const [statusTitles, setStatusTitles] = useState({});
	const [selectedGroupToEdit, setSelectedGroupToEdit] = useState(null);
	const [activeDragTask, setActiveDragTask] = useState(null);
	const [taskInputValues, setTaskInputValues] = useState(INITIAL_TASK_STATE);
	const [selectedTableColumnToEdit, setSelectedTableColumnToEdit] = useState(null);
	const [isShowCommentSidebar, setIsShowCommentSidebar] = useState(false);
	const [userSearchKey, setUserSearchKey] = useState('');
	const [isShowAddGroupModal, setIsShowAddGroupModal] = useState(false);
	const [isShowDeleteTaskModal, setIsShowDeleteTaskModal] = useState(false);
	const [selectedTaskToDelete, setSelectedTaskToDelete] = useState(null);
	const location = useLocation();
	const [showCommentPanel, setShowCommentPanel] = useState(false);
	const [taskId, setTaskId] = useState(null);
	const [stageVisibilityResponse, setStageVisibilityResponse] = useState([]);

	const [mixPanelTrakingEvent] = useMixPanelTrakingEventMutation();
	const [trigger, { data: allUsers }] = useLazyGetAllUserQuery();
	const [addTask, { isLoading: isAddTaskLoading }] = useAddTaskMutation();
	const [updateTask, { isLoading: isUpdateTaskLoading }] = useUpdateTaskMutation();
	const [deleteTask, { isLoading: isDeleteTaskLoading }] = useDeleteTaskMutation();
	const [updateAccordionStateOnAPI] = useUpdateOverviewAndStageVisibilityMutation();

	const statusRef = useRef(null);
	const priorityRef = useRef(null);
	const ownerRef = useRef(null);
	const taskNameRef = useRef(null);
	const taksRemarksRef = useRef(null);

	const isMountedRef = useRef(true);

	useEffect(() => {
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	// DnD Sensor Setup
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
				delay: 250,
				tolerance: 8
			}
		})
	);

	const resetEditStates = useCallback(() => {
		setSelectedTableColumnToEdit(null);
		setTaskInputValues(INITIAL_TASK_STATE);
	}, []);

	useMultipleOutsideClick({
		refs: [statusRef, priorityRef, ownerRef],
		conditions: [
			selectedTableColumnToEdit?.columnName === TASK_COLUMNS.STATUS,
			selectedTableColumnToEdit?.columnName === TASK_COLUMNS.PRIORITY,
			selectedTableColumnToEdit?.columnName === TASK_COLUMNS.OWNER
		],
		callback: resetEditStates
	});

	useEffect(() => {
		if (groupedTasks) {
			const initialTitles = {};
			Object.keys(groupedTasks).forEach((status) => {
				initialTitles[status] = status;
			});
			setStatusTitles(initialTitles);
		}
	}, [groupedTasks]);

	useEffect(() => {
		trigger({
			params: {
				email: userSearchKey,
				page: 0,
				size: 30
			}
		});
	}, [userSearchKey]);

	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const showComment = searchParams.get('showComment');
		const queryTaskId = searchParams.get('taskId');

		if (showComment === 'true' && queryTaskId) {
			setTaskId(queryTaskId);
			setShowCommentPanel(true);
		}
	}, [location.search]);

	// Initialize accordion states from projectDetails
	useEffect(() => {
		if (projectDetails?.stagesVisibilityJson) {
			try {
				const parsedStages = JSON.parse(projectDetails.stagesVisibilityJson);
				if (Array.isArray(parsedStages)) {
					setStageVisibilityResponse(parsedStages);
				}
			} catch {
				setStageVisibilityResponse([]);
			}
		} else if (projectDetails && Object.keys(groupedTasks || {}).length > 0) {
			// If no saved state, initialize all accordions as open
			const allGroupIds = Object.values(groupedTasks || {}).map((g) => g.groupId);
			const defaultStates = allGroupIds.map((gId) => ({ id: gId, value: 'OPEN' }));
			setStageVisibilityResponse(defaultStates);
		}
	}, [projectDetails?.stagesVisibilityJson, groupedTasks]);

	const isLoading = getLoadingState([isAddTaskLoading, isUpdateTaskLoading, isDeleteTaskLoading]);

	const isTaskColumnEditing = (taskId, columnName) => {
		return (
			selectedTableColumnToEdit?.id === taskId && selectedTableColumnToEdit?.columnName === columnName
		);
	};

	const isTaskNameEditing = (taskId) => {
		return (
			(selectedTableColumnToEdit?.id === taskId || taskInputValues?.tempId === taskId) &&
			isTaskColumnEditing(taskId, TASK_COLUMNS.TASK)
		);
	};

	const handleTaskInputChange = (field, value) => {
		setTaskInputValues((prev) => ({ ...prev, [field]: value }));
	};

	const handleUserSearch = (searchKey) => {
		setUserSearchKey(searchKey);
	};

	// Handle accordion toggle for all accordions
	const handleAccordionToggle = (groupId) => {
		setStageVisibilityResponse((prev) => {
			const allGroupIds = Object.values(groupedTasks || {}).map((g) => g.groupId);

			const completeStateArray = allGroupIds.map((gId) => {
				const existingState = prev.find((item) => parseInt(item?.id) === parseInt(gId));

				if (parseInt(gId) === parseInt(groupId)) {
					const currentState = existingState?.value || 'CLOSED';
					const newState = currentState === 'OPEN' ? 'CLOSED' : 'OPEN';
					return { id: gId, value: newState };
				} else {
					return { id: gId, value: existingState?.value || 'CLOSED' };
				}
			});

			updateAccordionStateOnAPI({
				projectId: id,
				params: {
					stageVisibilityJson: JSON.stringify(completeStateArray)
				}
			})
				.unwrap()
				.catch((error) => {
					toastError(error?.data?.message || 'Failed to update accordion state');
					setStageVisibilityResponse(prev);
				});

			return completeStateArray;
		});
	};

	// Create new Task row (Frontend only until add new value)
	const handleAddNewTaskRow = (status) => {
		const tempId = `new-task-${Date.now()}`;
		const updatedGroups = prepareNewTaskData(groupedTasks, status, tempId, groupedTasks[status].groupId);

		setGroupedTasks(updatedGroups);

		setTaskInputValues({
			...taskInputValues,
			taskGroupId: groupedTasks[status].groupId,
			isNewTaskRow: true,
			tempId: tempId
		});

		setSelectedTableColumnToEdit({
			tempId: tempId,
			groupId: groupedTasks[status].groupId,
			columnName: TASK_COLUMNS.TASK,
			isNewTaskRow: true
		});
	};

	const handleAddNewTask = () => {
		if (!taskInputValues?.name || taskInputValues?.name.trim() === '') {
			// Remove empty task row when no value is entered
			const updatedGroups = removeEmptyTaskRow(groupedTasks, taskInputValues.tempId);
			setGroupedTasks(updatedGroups);
			setTaskInputValues(INITIAL_TASK_STATE);
			setSelectedTableColumnToEdit(null);
			return;
		}

		addTask({
			projectId: id,
			body: {
				name: taskInputValues?.name,
				taskGroupId: taskInputValues?.taskGroupId
			}
		})
			.unwrap()
			.then(() => {
				if (isMountedRef.current) {
					refetchTasks();
					refetchProjectDetails();
					setTaskInputValues(INITIAL_TASK_STATE);
					setSelectedTableColumnToEdit(null);
				}
			})
			.catch((error) => {
				if (isMountedRef.current) {
					toastError(error?.data?.message || 'Failed to create task');
				}
			});
	};

	const handleUpdateTask = (body) => {
		const verifyRequestBody = isValidRequestBody(body, resetEditStates);
		if (!verifyRequestBody) {
			return;
		}

		updateTask({
			projectId: id,
			body
		})
			.unwrap()
			.then(() => {
				if (isMountedRef.current) {
					refetchTasks();
					setSelectedTableColumnToEdit(null);
					setTaskInputValues(INITIAL_TASK_STATE);
				}
			})
			.catch((error) => {
				if (isMountedRef.current) {
					toastError(error?.data?.message || 'Failed to update task');
				}
			});
	};

	const handleDeleteTask = (taskId) => {
		deleteTask({ taskId })
			.unwrap()
			.then(() => {
				refetchTasks();
				setIsShowDeleteTaskModal(false);
				setSelectedTaskToDelete(null);
				toastSuccess('Task deleted successfully');
			})
			.catch((error) => {
				toastError(error?.data?.message || 'Failed to delete task');
			});
	};

	// DnD handlers
	const handleDragStart = (event) => {
		const { active } = event;
		const activeId = active.id.toString();

		let foundTask = null;
		for (const groupKey of Object.keys(groupedTasks)) {
			const group = groupedTasks[groupKey];
			const task = group.groupData?.find((t) => (t.id || t.tempId)?.toString() === activeId);
			if (task) {
				foundTask = task;
				break;
			}
		}
		setActiveDragTask(foundTask);
	};

	const handleDragEnd = (event) => {
		const { active, over } = event;
		if (!active || !over) return;

		const activeId = active.id.toString();
		const overId = over.id.toString();

		// If overId is a group name, it's an empty group
		const isOverGroup = Object.keys(groupedTasks).includes(overId);

		// Find the group and index for active
		let activeGroup, activeIndex;
		for (const [group, groupData] of Object.entries(groupedTasks)) {
			const idx = groupData.groupData?.findIndex((t) => (t.id || t.tempId)?.toString() === activeId);
			if (idx !== -1) {
				activeGroup = group;
				activeIndex = idx;
				break;
			}
		}

		if (!activeGroup) return;

		if (isOverGroup) {
			// Move to empty group
			const { updatedGroups, newTaskData } = moveToEmptyGroup(
				groupedTasks,
				activeGroup,
				activeIndex,
				overId
			);

			updateTask({
				body: newTaskData,
				projectId: id
			});

			setGroupedTasks(updatedGroups);
		} else {
			// Find the group and index for over
			let overGroup, overIndex;
			for (const [group, groupData] of Object.entries(groupedTasks)) {
				const idx = groupData.groupData?.findIndex((t) => (t.id || t.tempId)?.toString() === overId);
				if (idx !== -1) {
					overGroup = group;
					overIndex = idx;
					break;
				}
			}
			if (!overGroup) return;

			if (activeGroup === overGroup) {
				// Reorder in the same group
				const { updatedGroups, newTaskData } = reorderWithinGroup(
					groupedTasks,
					activeGroup,
					activeIndex,
					overIndex
				);

				updateTask({
					body: newTaskData,
					projectId: id
				});

				setGroupedTasks(updatedGroups);
			} else {
				// Move to another group (non-empty)
				const { updatedGroups, newTaskData } = moveToNonEmptyGroup(
					groupedTasks,
					activeGroup,
					activeIndex,
					overGroup,
					overIndex
				);

				updateTask({
					body: newTaskData,
					projectId: id
				});

				setGroupedTasks(updatedGroups);
			}
		}
		setActiveDragTask(null);
	};

	// Dnd Handlers
	const prepareNewTaskData = (prevGroups, status, tempId, statusGroupId) => {
		const updatedGroups = { ...prevGroups };

		if (!updatedGroups[status].groupData) {
			updatedGroups[status].groupData = [];
		}

		updatedGroups[status].groupData = [
			...updatedGroups[status].groupData,
			{
				name: '',
				tempId: tempId,
				isNewTaskRow: true,
				taskGroupId: statusGroupId
			}
		];

		return updatedGroups;
	};

	const removeEmptyTaskRow = (groups, tempId) => {
		const updatedGroups = { ...groups };

		Object.keys(updatedGroups).forEach((group) => {
			if (updatedGroups[group].groupData) {
				updatedGroups[group].groupData = updatedGroups[group].groupData.filter(
					(task) => task.tempId !== tempId
				);
			}
		});

		return updatedGroups;
	};

	const moveToEmptyGroup = (groups, activeGroup, activeIndex, overId) => {
		const updatedGroups = { ...groups };
		const sourceTasks = [...updatedGroups[activeGroup].groupData];
		const [movedTask] = sourceTasks.splice(activeIndex, 1);

		if (!updatedGroups[overId].groupData) {
			updatedGroups[overId].groupData = [];
		}

		const destTasks = [...updatedGroups[overId].groupData];
		const newSl = calculateNewSl(sourceTasks, destTasks.length);

		updatedGroups[activeGroup].groupData = sourceTasks;
		updatedGroups[overId].groupData = [...destTasks, movedTask];

		return {
			updatedGroups,
			newTaskData: {
				id: movedTask.id || movedTask.tempId,
				sl: Number(newSl).toFixed(2),
				taskGroupId: updatedGroups[overId].groupId
			}
		};
	};

	const reorderWithinGroup = (groups, activeGroup, activeIndex, overIndex) => {
		const updatedGroups = { ...groups };
		const newTasks = arrayMove(updatedGroups[activeGroup].groupData, activeIndex, overIndex);
		const newSl = calculateNewSl(updatedGroups[activeGroup].groupData, overIndex);

		updatedGroups[activeGroup].groupData = newTasks;

		return {
			updatedGroups,
			newTaskData: {
				id:
					updatedGroups[activeGroup].groupData[overIndex].id ||
					updatedGroups[activeGroup].groupData[overIndex].tempId,
				sl: Number(newSl).toFixed(2),
				taskGroupId: updatedGroups[activeGroup].groupId
			}
		};
	};

	const moveToNonEmptyGroup = (groups, activeGroup, activeIndex, overGroup, overIndex) => {
		const updatedGroups = { ...groups };
		const sourceTasks = [...updatedGroups[activeGroup].groupData];
		const destTasks = [...updatedGroups[overGroup].groupData];
		const [movedTask] = sourceTasks.splice(activeIndex, 1);

		const newSl = calculateNewSl(destTasks, overIndex);

		destTasks.splice(overIndex, 0, movedTask);

		updatedGroups[activeGroup].groupData = sourceTasks;
		updatedGroups[overGroup].groupData = destTasks;

		return {
			updatedGroups,
			newTaskData: {
				id: movedTask.id || movedTask.tempId,
				sl: Number(newSl).toFixed(2),
				taskGroupId: updatedGroups[overGroup].groupId
			}
		};
	};

	const renderTaskColumn = (task, group) => {
		if (!isHideColumn(TASK_COLUMNS.TASK, selectedColumnToHide)) return null;

		return (
			<td
				className={`${isTaskNameEditing(task?.id) ? 'focus-active' : ''} task-column`}
				onClick={() => {
					if (isLoading) return;
					setSelectedTableColumnToEdit({
						...task,
						groupId: groupedTasks[group]?.groupId,
						columnName: TASK_COLUMNS.TASK
					});
					setTaskInputValues({
						...taskInputValues,
						name: task?.name
					});
					setTimeout(() => {
						if (taskNameRef.current) {
							taskNameRef.current.style.height = 'auto';
							taskNameRef.current.style.height = `${taskNameRef.current.scrollHeight}px`;
						}
					}, 0);
				}}
			>
				{isTaskNameEditing(task?.id) ? (
					<textarea
						ref={taskNameRef}
						type="text"
						name="name"
						placeholder="Task Name"
						value={taskInputValues?.name}
						onChange={(e) => {
							handleTaskInputChange('name', e.target.value);
							taskNameRef.current.style.height = 'auto';
							taskNameRef.current.style.height = `${taskNameRef.current.scrollHeight}px`;
						}}
						style={{
							overflow: 'hidden',
							resize: 'none'
						}}
						onBlur={() => {
							if (taskInputValues?.isNewTaskRow) {
								handleAddNewTask();
							} else {
								handleUpdateTask({
									group: groupedTasks[group]?.groupId,
									id: task?.id,
									name: taskInputValues?.name
								});
							}
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								if (taskInputValues?.isNewTaskRow) {
									handleAddNewTask();
								} else {
									e.preventDefault();
									handleUpdateTask({
										id: task?.id,
										groupId: groupedTasks[group]?.groupId,
										name: taskInputValues?.name
									});
								}
							}
						}}
						autoFocus={true}
						wrapperClassName="form-group mb-0"
						onFocus={(e) => {
							const length = e.target.value.length;
							e.target.setSelectionRange(length, length);
						}}
					/>
				) : (
					<p className="regular-14 gray_dark_01">{task?.name || ''}</p>
				)}
			</td>
		);
	};

	const renderCommentsColumn = (task, group) => {
		if (!isHideColumn(TASK_COLUMNS.COMMENTS, selectedColumnToHide)) return null;

		return (
			<td
				className="task-comments-column text-center"
				onClick={() => {
					if (isLoading) return;
					setSelectedTableColumnToEdit({
						...task,
						groupId: groupedTasks[group]?.groupId,
						columnName: TASK_COLUMNS.COMMENTS
					});
					setIsShowCommentSidebar(true);
					mixPanelTrakingEvent({
						type: MIX_PANEL_EVENTS.PROJECT_TASK_COMMENT_PANEL_ON_CLICK,
						body: {
							taskId: task?.id,
							isOpenMessage: true,
							projectId: id
						}
					});
				}}
			>
				<div className="total-comments">
					<CommonImage
						className="cursor-pointer"
						src="/icons/comments-gray.svg"
						width={24}
						height={24}
					/>
					{task?.noOfUnreadMessage > 0 && (
						<span className="total-comments-count">{task?.noOfUnreadMessage}</span>
					)}
				</div>
				{isShowCommentSidebar && selectedTableColumnToEdit?.id === task?.id && (
					<div onClick={(e) => e.stopPropagation()}>
						<SampleComment
							sampleRequestId={null}
							postTypeFilter={'PROJECT_TASK_COMMENT'}
							topic={'PROJECT_TASK_CONVERSATION'}
							productId={task?.id}
							showComment={isShowCommentSidebar}
							setShowComment={(value) => {
								setIsShowCommentSidebar(value);
								setSelectedTableColumnToEdit(null);
							}}
							onSuccess={() => {
								refetchTasks();
								setIsShowCommentSidebar(false);
							}}
							addPostUrl={'addNewProjectPost'}
							getPostUrl={'getProjectConversation'}
							isProject={true}
						/>
					</div>
				)}
			</td>
		);
	};

	const renderOwnerColumn = (task, group) => {
		if (!isHideColumn(TASK_COLUMNS.OWNER, selectedColumnToHide)) return null;

		return (
			<td
				className="owner-column text-center"
				onClick={() => {
					if (isLoading) return;
					setSelectedTableColumnToEdit({
						...task,
						groupId: groupedTasks[group]?.groupId,
						columnName: TASK_COLUMNS.OWNER
					});
				}}
			>
				{isTaskColumnEditing(task?.id, TASK_COLUMNS.OWNER) ? (
					<div ref={ownerRef}>
						<CustomDropdown
							title="Select Owner"
							items={allUsers?.map((user) => ({
								id: user?.id,
								name: user?.name,
								value: user?.name
							}))}
							selectedItem={task?.ownerResponse?.name}
							onItemClick={(item) => {
								handleUpdateTask({
									id: task?.id,
									ownerId: item?.id,
									groupId: groupedTasks[group]?.groupId
								});
							}}
							isShowSearch={true}
							searchPlaceholder="Search Owner"
							onItemSearch={(searchKey) => handleUserSearch(searchKey)}
						/>
					</div>
				) : (
					<div className="owner-img">
						<CustomTooltip arrowCenter={true} text={task.ownerResponse?.name || 'Add Owner'}>
							{task.ownerResponse ? (
								<CommonImage
									className="cursor-pointer rounded-circle"
									src={task.ownerResponse?.profilePicDocUrl}
									width={24}
									height={24}
									fallbackSrc={'/images/pro_pic_default.svg'}
									isStatic={task.ownerResponse?.profilePicDocUrl ? false : true}
								/>
							) : (
								<CommonImage
									className="cursor-pointer rounded-circle"
									src="/icons/add-people.svg"
									width={24}
									height={24}
								/>
							)}
						</CustomTooltip>
					</div>
				)}
			</td>
		);
	};

	const renderStartDateColumn = (task, group) => {
		if (!isHideColumn(TASK_COLUMNS.START_DATE, selectedColumnToHide)) return null;

		return (
			<td
				className="start-date-column"
				onClick={() => {
					if (isLoading) return;
					setSelectedTableColumnToEdit({
						...task,
						groupId: groupedTasks[group]?.groupId,
						columnName: TASK_COLUMNS.START_DATE
					});
					setTaskInputValues({
						...taskInputValues,
						startDate: task?.startDate
					});
				}}
			>
				{isTaskColumnEditing(task?.id, TASK_COLUMNS.START_DATE) ? (
					<div>
						<InputField
							type="date"
							name="startDate"
							value={taskInputValues?.startDate}
							onChange={(e) => handleTaskInputChange('startDate', e.target.value)}
							onBlur={() => {
								handleUpdateTask({
									id: task?.id,
									groupId: groupedTasks[group]?.groupId,
									startDate: taskInputValues?.startDate
								});
							}}
							wrapperClassName="form-group mb-0"
							autoFocus
						/>
					</div>
				) : (
					<p className="regular-14 gray_dark_01">
						{task?.startDate ? onGetLocalFromUtc(task?.startDate, 'DD-MMM, YYYY') : ''}{' '}
					</p>
				)}
			</td>
		);
	};

	const renderDueDateColumn = (task, group) => {
		if (!isHideColumn(TASK_COLUMNS.DUE_DATE, selectedColumnToHide)) return null;

		return (
			<td
				className="due-date-column"
				onClick={() => {
					if (isLoading) return;
					setSelectedTableColumnToEdit({
						...task,
						groupId: groupedTasks[group]?.groupId,
						columnName: TASK_COLUMNS.DUE_DATE
					});
				}}
			>
				{isTaskColumnEditing(task?.id, TASK_COLUMNS.DUE_DATE) ? (
					<div>
						<InputField
							type="date"
							name="dueDate"
							value={taskInputValues?.dueDate}
							onChange={(e) => handleTaskInputChange('dueDate', e.target.value)}
							onBlur={() => {
								handleUpdateTask({
									id: task?.id,
									groupId: groupedTasks[group]?.groupId,
									dueDate: taskInputValues?.dueDate
								});
							}}
							autoFocus
							wrapperClassName="form-group mb-0"
						/>
					</div>
				) : (
					<p className="regular-14 gray_dark_01">
						{task?.dueDate ? onGetLocalFromUtc(task?.dueDate, 'DD-MMM, YYYY') : ''}
					</p>
				)}
			</td>
		);
	};

	const renderStatusColumn = (task, group) => {
		if (!isHideColumn(TASK_COLUMNS.STATUS, selectedColumnToHide)) return null;

		return (
			<td
				className="status-column"
				onClick={() => {
					if (isLoading) return;
					setSelectedTableColumnToEdit({
						...task,
						groupId: groupedTasks[group]?.groupId,
						columnName: TASK_COLUMNS.STATUS
					});
				}}
				style={{
					backgroundColor: task?.taskStatusTagResponse?.colorCode
				}}
			>
				{isTaskColumnEditing(task?.id, TASK_COLUMNS.STATUS) && (
					<div ref={statusRef}>
						<TaskDndTagsModal
							tagsType="PROJECT_TASK_STATUS"
							selectedTableColumnToEdit={selectedTableColumnToEdit}
							id={id}
							updateTask={updateTask}
							refetchTasks={refetchTasks}
							resetEditStates={resetEditStates}
							requestBodyKey="taskStatusTagRequest"
							refetchProjectDetails={refetchProjectDetails}
							refetchProjectTags={refetchProjectTags}
						/>
					</div>
				)}
				<p className="regular-14 text-white weight-600">{task.taskStatusTagResponse?.text || ''}</p>
			</td>
		);
	};

	const renderPriorityColumn = (task, group) => {
		if (!isHideColumn(TASK_COLUMNS.PRIORITY, selectedColumnToHide)) return null;

		return (
			<td
				className="priority-column"
				onClick={() => {
					if (isLoading) return;
					setSelectedTableColumnToEdit({
						...task,
						groupId: groupedTasks[group]?.groupId,
						columnName: TASK_COLUMNS.PRIORITY
					});
				}}
				style={{
					backgroundColor: task?.taskPriorityTagResponse?.colorCode
				}}
			>
				{isTaskColumnEditing(task?.id, TASK_COLUMNS.PRIORITY) && (
					<div ref={priorityRef}>
						<TaskDndTagsModal
							tagsType="PROJECT_TASK_PRIORITY"
							selectedTableColumnToEdit={selectedTableColumnToEdit}
							id={id}
							updateTask={updateTask}
							refetchTasks={refetchTasks}
							resetEditStates={resetEditStates}
							requestBodyKey="taskPriorityTagRequest"
							refetchProjectTags={refetchProjectTags}
						/>
					</div>
				)}
				<p className="regular-14 text-white weight-600">{task.taskPriorityTagResponse?.text || ''}</p>
			</td>
		);
	};

	const renderRemarksColumn = (task, group) => {
		if (!isHideColumn(TASK_COLUMNS.REMARKS, selectedColumnToHide)) return null;

		return (
			<td
				className={`${isTaskColumnEditing(task?.id, TASK_COLUMNS.REMARKS) ? 'focus-active' : ''} remarks-column`}
				onClick={() => {
					if (isLoading) return;
					setSelectedTableColumnToEdit({
						...task,
						groupId: groupedTasks[group]?.groupId,
						columnName: TASK_COLUMNS.REMARKS
					});
					setTaskInputValues({
						...taskInputValues,
						remarks: task?.remarks
					});
					setTimeout(() => {
						if (taksRemarksRef.current) {
							taksRemarksRef.current.style.height = 'auto';
							taksRemarksRef.current.style.height = `${taksRemarksRef.current.scrollHeight}px`;
						}
					}, 0);
				}}
			>
				{isTaskColumnEditing(task?.id, TASK_COLUMNS.REMARKS) ? (
					<div>
						<textarea
							rows={1}
							ref={taksRemarksRef}
							type="text"
							name="remarks"
							value={taskInputValues?.remarks}
							onChange={(e) => {
								taksRemarksRef.current.style.height = 'auto';
								taksRemarksRef.current.style.height = `${taksRemarksRef.current.scrollHeight}px`;
								handleTaskInputChange('remarks', e.target.value);
							}}
							onBlur={() => {
								handleUpdateTask({
									id: task?.id,
									groupId: groupedTasks[group]?.groupId,
									remarks: taskInputValues?.remarks
								});
							}}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									e.preventDefault();
									handleUpdateTask({
										id: task?.id,
										groupId: groupedTasks[group]?.groupId,
										remarks: taskInputValues?.remarks
									});
								}
							}}
							autoFocus
							wrapperClassName="form-group mb-0"
							style={{ overflow: 'hidden' }}
							onFocus={(e) => {
								const length = e.target.value.length;
								e.target.setSelectionRange(length, length);
							}}
						/>
					</div>
				) : (
					<p className="regular-14 gray_dark_01">{task?.remarks || ''}</p>
				)}
			</td>
		);
	};

	const renderActionColumn = (task) => {
		return (
			<td className="action-column">
				<div className="option">
					<div className="dropdown">
						<button
							className="btn btn-default dropdown-toggle border-0"
							type="button"
							id={`menu-${task?.id}`}
							data-toggle="dropdown"
							aria-expanded="false"
						>
							<CommonImage src="/icons/more_icon.svg" alt="more_icon" />
						</button>
						<ul
							className="dropdown-menu dropdown-menu-right shadow-lg"
							role="menu"
							aria-labelledby={`menu-${task?.id}`}
							x-placement="bottom-end"
						>
							<li
								role="presentation"
								className="px-4 pb-3"
								onClick={() => {
									setIsShowDeleteTaskModal(true);
									setSelectedTaskToDelete(task);
								}}
							>
								<span role="menuitem" tabIndex={-1} className="font-weight-normal text-red">
									Delete
								</span>
							</li>
						</ul>
					</div>
				</div>
			</td>
		);
	};

	const renderTaskRows = (group) => {
		if (!groupedTasks[group] || !groupedTasks[group]?.groupData?.length) {
			return (
				<SortableTaskRow key={group} task={{ id: group }}>
					<td
						colSpan={9}
						style={{
							display: 'hidden'
						}}
					></td>
				</SortableTaskRow>
			);
		}

		return groupedTasks[group]?.groupData.map((task) => (
			<SortableTaskRow key={task.id || task.tempId} task={task}>
				{renderTaskColumn(task, group)}
				{renderCommentsColumn(task, group)}
				{renderOwnerColumn(task, group)}
				{renderStartDateColumn(task, group)}
				{renderDueDateColumn(task, group)}
				{renderStatusColumn(task, group)}
				{renderPriorityColumn(task, group)}
				{renderRemarksColumn(task, group)}
				{renderActionColumn(task)}
			</SortableTaskRow>
		));
	};

	const tableHeaderContent = () => {
		return (
			<tr>
				{PROJECT_DETAILS_TABLE_COLUMNS.map(
					(column) =>
						isHideColumn(column.id, selectedColumnToHide) && (
							<th key={column.id}>{column.name}</th>
						)
				)}
				<th>Action</th>
			</tr>
		);
	};

	const accordionHeaderContent = (group) => {
		return (
			<div className="ml-2 d-flex gap-2 items-center">
				<p className="medium-14" style={{ color: groupedTasks[group]?.colorCode }}>
					{statusTitles[group]}
				</p>
				<CommonImage
					src="/icons/edit-tool.svg"
					width={20}
					height={20}
					onClick={(e) => {
						e.stopPropagation();
						setIsShowAddGroupModal(true);
						setSelectedGroupToEdit(groupedTasks[group]);
					}}
				/>
			</div>
		);
	};

	const accordionBodyContent = (group) => {
		return (
			<div
				id={`tableCollapse-${group}`}
				className="collapse show"
				aria-labelledby={`heading-${group}`}
				data-parent="#accordionExample"
			>
				<div
					className="project-details-table"
					style={{
						borderLeft: `5px solid ${groupedTasks[group]?.colorCode || '#cc2121'}`
					}}
				>
					<SortableContext
						items={
							groupedTasks[group].groupData?.length > 0
								? groupedTasks[group].groupData.map((task) => task.id || task.tempId)
								: [group]
						}
						strategy={verticalListSortingStrategy}
					>
						<table className="table todo-table with-border no-hover height-sm hover">
							{/* Table Header */}
							<thead>{tableHeaderContent()}</thead>
							{/* Table Content */}
							<tbody>
								{renderTaskRows(group)}
								{/* Add New Task Row */}
								<tr className="add-task-row">
									<td colSpan={9} className="text-left">
										<button
											className="button text"
											onClick={() => {
												handleAddNewTaskRow(group);
											}}
										>
											+ Add new task
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</SortableContext>
				</div>
			</div>
		);
	};

	const renderAddGroupButton = () => {
		return (
			<div className="add-new-button mt-4">
				<button className="button outline size32" onClick={() => setIsShowAddGroupModal(true)}>
					<svg
						fill="none"
						height="24"
						viewBox="0 0 24 24"
						width="24"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M18 13H13V18C13 18.55 12.55 19 12 19C11.45 19 11 18.55 11 18V13H6C5.45 13 5 12.55 5 12C5 11.45 5.45 11 6 11H11V6C11 5.45 11.45 5 12 5C12.55 5 13 5.45 13 6V11H18C18.55 11 19 11.45 19 12C19 12.55 18.55 13 18 13Z"
							fill="white"
						></path>
					</svg>
					<span className="ml-1">Add new group</span>
				</button>
			</div>
		);
	};

	return (
		<LoaderComponent isLoading={isLoading}>
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
			>
				<div className="task-table-accordion">
					{Object.keys(groupedTasks || {}).map((group, index) => (
						<ProjectAccordionWraper
							key={index}
							accordionId={`heading-${index}`}
							containerKey={group}
							iconColor={groupedTasks[group]?.colorCode}
							headerContent={accordionHeaderContent(group)}
							bodyContent={accordionBodyContent(group)}
							groupId={groupedTasks[group]?.groupId}
							stageVisibilityResponse={stageVisibilityResponse}
							onAccordionToggle={handleAccordionToggle}
						/>
					))}
					{/* Add New Group Button */}
					{renderAddGroupButton()}
				</div>
				{isShowAddGroupModal && (
					<CreateAndEditGroup
						selectedGroupToEdit={selectedGroupToEdit}
						onClose={() => {
							setIsShowAddGroupModal(false);
							setSelectedGroupToEdit(null);
						}}
						projectId={id}
						refetchGroups={refetchGroups}
						refetchTasks={refetchTasks}
					/>
				)}
				{/* Drag Overlay Show When Dragging */}
				<DragOverlayForTable activeDragTask={activeDragTask} />
			</DndContext>
			{isShowDeleteTaskModal && selectedTaskToDelete && (
				<DeleteConfirmationModal
					onClose={() => setIsShowDeleteTaskModal(false)}
					onConfirm={() => handleDeleteTask(selectedTaskToDelete?.id)}
					title="Remove Task"
					description="Are you sure about removing this task?"
				/>
			)}
			{showCommentPanel && taskId && (
				<SampleComment
					productId={taskId}
					showComment={showCommentPanel}
					setShowComment={setShowCommentPanel}
					postTypeFilter="PROJECT_TASK_COMMENT"
					topic="PROJECT_TASK_CONVERSATION"
					getPostUrl="getProjectConversation"
					addPostUrl="addNewProjectPost"
					isProject={true}
					onSuccess={() => {
						refetchTasks();
						setIsShowCommentSidebar(false);
					}}
				/>
			)}
		</LoaderComponent>
	);
};

export default ProjectDetailsTableView;
